/**
 * Payment API Service
 *
 * Handles all payment-related API calls to the backend PaymentsController.
 * Integrates with Stripe for payment processing.
 */

import api from '@/lib/axios';
import {
    StripeConfigResponse,
    PaymentIntentResponse,
    CreatePaymentIntentRequest,
    CustomPaymentIntentRequest,
    SetupIntentResponse,
    PaymentMethodsResponse,
    CheckoutPaymentStatusResponse,
} from '@/types/payments';
import { CancelSubscriptionApiResponse } from '@/types/reservations';

// =============================================================================
// Configuration
// =============================================================================

/**
 * Get Stripe configuration (publishable key) for frontend initialization.
 * Call this first before using any Stripe components.
 */
export const getStripeConfig = async (): Promise<StripeConfigResponse> => {
    const response = await api.get<StripeConfigResponse>('/customer/payments/config');
    return response.data;
};

// =============================================================================
// Payment Intents (One-time Payments)
// =============================================================================

/**
 * Create a payment intent for the current cart.
 * Returns a client secret to confirm payment on the frontend.
 *
 * @param request - Payment intent request parameters
 * @param sessionId - Optional session ID override. If not provided, uses the
 *                    current session from localStorage (consistent with cart API).
 */
export const createPaymentIntent = async (
    request: CreatePaymentIntentRequest = {},
    // sessionId parameter kept for API compatibility during transition period.
    // HARD-011: session is now carried by the HttpOnly stork_sid cookie; this
    // parameter is ignored.  Remove in the next cleanup pass.
    _sessionId?: string
): Promise<PaymentIntentResponse> => {
    const sourceOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;

    const response = await api.post<PaymentIntentResponse>(
        '/customer/payments/create-intent',
        {
            ...request,
            sourceOrigin: request.sourceOrigin ?? sourceOrigin,
        }
    );
    return response.data;
};

/**
 * Create a custom payment intent for a specific amount (not cart-based).
 * Useful for additional charges like damage fees, extensions, or late fees.
 * Requires authentication.
 */
export const createCustomPaymentIntent = async (
    request: CustomPaymentIntentRequest
): Promise<PaymentIntentResponse> => {
    const response = await api.post<PaymentIntentResponse>(
        '/customer/payments/create-intent/custom',
        request
    );
    return response.data;
};

// HARD-011: X-Session-ID header sending removed; stork_sid cookie is sent
// automatically by the axios instance (withCredentials: true).
// The sessionId parameters below are kept for API compatibility during
// transition and will be removed in the next cleanup pass.

export const reconcileCheckoutPayment = async (
    paymentIntentId: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _sessionId?: string
): Promise<CheckoutPaymentStatusResponse> => {
    const response = await api.post<CheckoutPaymentStatusResponse>(
        '/customer/payments/reconcile',
        { paymentIntentId }
    );

    return response.data;
};

export const getCheckoutPaymentStatus = async (
    paymentIntentId: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _sessionId?: string
): Promise<CheckoutPaymentStatusResponse> => {
    const response = await api.get<CheckoutPaymentStatusResponse>(
        '/customer/payments/status',
        {
            params: { paymentIntentId },
        }
    );

    return response.data;
};

// =============================================================================
// Setup Intents (Save Card for Future Use)
// =============================================================================

/**
 * Create a setup intent to save a payment method without charging.
 * Use this for saving cards for future deposit charges or recurring payments.
 * Requires authentication.
 *
 * When saving/updating a card in the context of a specific monthly rental, pass
 * the rental's `bookingId` so the backend creates the SetupIntent on the booking's
 * location Stripe account — matching the subscription's account so the card attach
 * succeeds for per-location/Spain tenants (GAP2). Omit it for a generic save-card
 * with no rental context; the backend then defaults to the global account.
 */
export const createSetupIntent = async (bookingId?: string): Promise<SetupIntentResponse> => {
    const response = await api.post<SetupIntentResponse>(
        '/customer/payments/setup-intent',
        bookingId ? { bookingId } : undefined
    );
    return response.data;
};

// =============================================================================
// Saved Payment Methods
// =============================================================================

/**
 * Get the customer's saved payment methods.
 * Requires authentication.
 */
export const getPaymentMethods = async (): Promise<PaymentMethodsResponse> => {
    const response = await api.get<PaymentMethodsResponse>('/customer/payments/payment-methods');
    return response.data;
};

/**
 * Set a payment method as the default for the customer.
 * Requires authentication.
 */
export const setDefaultPaymentMethod = async (paymentMethodId: string): Promise<{ success: boolean }> => {
    const response = await api.post<{ success: boolean }>(
        `/customer/payments/payment-methods/${paymentMethodId}/default`
    );
    return response.data;
};

/**
 * Remove a saved payment method.
 * Requires authentication.
 */
export const deletePaymentMethod = async (paymentMethodId: string): Promise<{ success: boolean }> => {
    const response = await api.delete<{ success: boolean }>(
        `/customer/payments/payment-methods/${paymentMethodId}`
    );
    return response.data;
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format amount for display (e.g., 25.99 -> "€25.99")
 */
export const formatPaymentAmount = (amount: number, currency: string = 'eur'): string => {
    return new Intl.NumberFormat('en-IE', {
        style: 'currency',
        currency: currency.toUpperCase(),
    }).format(amount);
};

/**
 * Get card brand display name
 */
export const getCardBrandDisplayName = (brand?: string): string => {
    const brands: Record<string, string> = {
        visa: 'Visa',
        mastercard: 'Mastercard',
        amex: 'American Express',
        discover: 'Discover',
        diners: 'Diners Club',
        jcb: 'JCB',
        unionpay: 'UnionPay',
    };
    return brands[brand?.toLowerCase() || ''] || brand || 'Card';
};

/**
 * Check if a card is expired
 */
export const isCardExpired = (expMonth?: number, expYear?: number): boolean => {
    if (!expMonth || !expYear) return false;
    const now = new Date();
    const expDate = new Date(expYear, expMonth - 1); // Month is 0-indexed
    return expDate < now;
};

// =============================================================================
// Subscription Cancellation
// =============================================================================

/**
 * Cancellation preview response type (matches backend CancellationPreviewResponse)
 *
 * Cascading Tier Fallback Logic:
 * - 3-month subscriptions → Fall back to short-term tiered pricing
 * - 6-month subscriptions → Fall back to 3-month monthly rate
 * - 12-month subscriptions → Fall back to 6-month monthly rate
 */
export interface CancellationPreviewResponse {
    success: boolean;
    errorMessage?: string;
    withinCommitmentPeriod: boolean;
    earlyTerminationCharge?: number;
    tierFallbackDetails?: {
        daysRented: number;
        monthsPaid: number;
        /** The tier used for fallback: "Short-term tiered", "3-month rate", or "6-month rate" */
        fallbackTierName: string;
        /** Monthly rate for fallback (null for short-term tiered) */
        fallbackMonthlyRate: number | null;
        /** What the customer would have paid at the fallback tier */
        fallbackPrice: number;
        /** @deprecated Use fallbackPrice instead */
        shortTermTieredPrice: number;
        subscriptionPaymentsMade: number;
        wasSubscriptionBetterDeal: boolean;
        savingsAmount: number;
        penaltyAmount: number;
    };
    commitmentEndDate?: string;
    daysRemaining?: number;
}

/**
 * Get a preview of cancellation charges for a subscription.
 * Use this to show customers what charges would apply before confirming cancellation.
 *
 * @param customerBookingId - The ID of the customer booking (reservation)
 */
export const getCancellationPreview = async (
    customerBookingId: string
): Promise<CancellationPreviewResponse> => {
    const response = await api.get<CancellationPreviewResponse>(
        `/customer/monthly-rentals/${customerBookingId}/preview-cancellation`
    );
    return response.data;
};

/**
 * Cancel a subscription rental.
 * Returns tier fallback penalty details if cancelling before minimum commitment.
 *
 * @param customerBookingId - The ID of the customer booking (reservation)
 * @param cancelImmediately - If true, cancels immediately; if false, cancels at period end
 */
export const cancelSubscription = async (
    customerBookingId: string,
    cancelImmediately: boolean = false
): Promise<CancelSubscriptionApiResponse> => {
    const response = await api.post<CancelSubscriptionApiResponse>(
        `/customer/monthly-rentals/${customerBookingId}/cancel`,
        { cancelImmediately }
    );
    return response.data;
};
